import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  distinctUntilChanged,
  EMPTY,
  from,
  map,
  of,
  repeat,
  shareReplay,
  Subject,
  concatMap,
  switchMap,
  tap,
  toArray,
  throwError,
} from 'rxjs';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { RequestModel } from '@core/models/request.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectedIds } from '@common/types';
import { XAlertService } from '../x-alert.service';
import { catchError } from 'rxjs/operators';
import { Params, Router } from '@angular/router';
import { Nullable } from '@core/helpers';
import { Location } from '@angular/common';
import { AccountService } from '@core/services/account/account.service';
import { OfflineSnapshotRepository } from '../offline/offline-snapshot.repository';
import type { OfflineViewState } from '../offline/offline-snapshot.types';
import { environment } from '@environment/environment';
import type { MainRequestFilesPayload } from '@core/types';
import { isOfflineFallbackError } from '../offline/offline-fallback.helpers';

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  private readonly router = inject(Router);
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly requestNumber$ = new Subject<string>();
  private readonly reload$ = new Subject<void>();
  private readonly location = inject(Location);
  private xAlertService = inject(XAlertService);
  private readonly accountService = inject(AccountService);
  private readonly offlineSnapshotRepository = inject(
    OfflineSnapshotRepository,
  );
  private readonly _$listOfflineState = signal<OfflineViewState>('online');
  private readonly _$listSnapshotFetchedAt = signal<string | null>(null);
  private readonly _$requestOfflineState = signal<OfflineViewState>('online');
  private readonly _$requestSnapshotFetchedAt = signal<string | null>(null);
  readonly $listOfflineState = this._$listOfflineState.asReadonly();
  readonly $listSnapshotFetchedAt = this._$listSnapshotFetchedAt.asReadonly();
  readonly $requestOfflineState = this._$requestOfflineState.asReadonly();
  readonly $requestSnapshotFetchedAt =
    this._$requestSnapshotFetchedAt.asReadonly();
  private lastListParams: Params | null = null;
  readonly request$ = this.requestNumber$.pipe(
    switchMap(requestNumber =>
      this.requestsApiService.getRequest$(requestNumber).pipe(
        tap(() => {
          this._$requestOfflineState.set('online');
          this._$requestSnapshotFetchedAt.set(null);
        }),
        catchError(error =>
          this.getCachedRequestOnFailure(requestNumber, error),
        ),
        repeat({ delay: () => this.reload$ }),
      ),
    ),
    catchError(err => {
      // Во время anonymous login flow оставляем пользователя на deep link:
      // внешний auth.js по X_AUTH_LOGIN_SUCCESS делает window.location.reload(),
      // поэтому после входа этот же request stream будет создан заново.
      if (
        this.accountService.$authState() === 'anonymous' &&
        err?.status === 401
      ) {
        return EMPTY;
      }

      this.xAlertService.error(err?.error?.message || 'Error');
      void this.router.navigate(['/']);

      return EMPTY;
    }),
    map(request => new RequestModel(request)),
    shareReplay(1),
  );

  readonly requestId$ = this.request$.pipe(
    map(({ id }) => id),
    distinctUntilChanged(),
  );
  readonly $request = toSignal(this.request$) as Signal<RequestModel>;
  readonly $requestId = toSignal(this.requestId$) as Signal<number>;

  private getCachedRequestOnFailure(
    requestNumber: string,
    error: HttpErrorResponse,
  ) {
    const currentUser = this.accountService.$currentUser();

    if (!currentUser || !isOfflineFallbackError(error)) {
      return throwError(() => error);
    }

    this._$requestOfflineState.set('degraded');

    return from(
      this.offlineSnapshotRepository.getRequestSummary(
        environment.xOutkeepInfrastructureEnv,
        currentUser.id,
        requestNumber,
      ),
    ).pipe(
      switchMap(snapshot => {
        if (!snapshot) {
          this._$requestOfflineState.set('unavailable');

          return throwError(() => error);
        }

        this._$requestOfflineState.set('snapshot');
        this._$requestSnapshotFetchedAt.set(snapshot.fetchedAt);

        return of(snapshot.payload.request);
      }),
    );
  }

  private readonly _$loadedRequests = signal<RequestModel[] | null>([]);
  private readonly _$loadedRequestsCount = signal<number | null>(0);
  private readonly _$selectedRequestsIds = signal<SelectedIds | null>({});

  getAllRequestsByParam$(params: Params) {
    const parsedParams = this.mapParamsToRqQuery(params);

    this.lastListParams = parsedParams;
    const currentUser = this.accountService.$currentUser();

    return this.requestsApiService.getAllRequests$(parsedParams).pipe(
      switchMap(response => {
        if (!currentUser) {
          return of(response);
        }

        return from(
          this.offlineSnapshotRepository.saveRequestList(
            environment.xOutkeepInfrastructureEnv,
            currentUser.id,
            { params: parsedParams, response },
          ),
        ).pipe(
          catchError(() => of(undefined)),
          map(() => response),
        );
      }),
      tap(() => {
        this.addQueryStringToUrl(parsedParams);
        this._$listOfflineState.set('online');
        this._$listSnapshotFetchedAt.set(null);
      }),
      catchError(error => {
        if (!currentUser || !isOfflineFallbackError(error)) {
          return throwError(() => error);
        }

        this._$listOfflineState.set('degraded');

        return from(
          this.offlineSnapshotRepository.getRequestList(
            environment.xOutkeepInfrastructureEnv,
            currentUser.id,
            parsedParams,
          ),
        ).pipe(
          switchMap(snapshot => {
            if (!snapshot) {
              this._$listOfflineState.set('unavailable');

              return throwError(() => error);
            }

            this.addQueryStringToUrl(parsedParams);
            this._$listOfflineState.set('snapshot');
            this._$listSnapshotFetchedAt.set(snapshot.fetchedAt);

            return of(snapshot.payload);
          }),
        );
      }),
    );
  }

  async saveCurrentListMainFiles(
    mainFiles: MainRequestFilesPayload,
  ): Promise<void> {
    const currentUser = this.accountService.$currentUser();

    if (!currentUser || !this.lastListParams) {
      return;
    }

    await this.offlineSnapshotRepository.saveRequestListMainFiles(
      environment.xOutkeepInfrastructureEnv,
      currentUser.id,
      this.lastListParams,
      mainFiles,
    );
  }

  private addQueryStringToUrl(params: Params) {
    const baseUrl = this.router.url.split('?')[0];
    const queryString = new URLSearchParams(params).toString();

    if (queryString) {
      const separator = baseUrl.includes('?') ? '&' : '?';

      this.location.replaceState(baseUrl + separator + queryString);
    } else {
      this.location.replaceState(baseUrl);
    }
  }

  private mapParamsToRqQuery(params: Params): Params {
    const parsedParams = { ...params };
    const rqQuery = this.parseRqQuery(parsedParams['rqQuery']);

    if (Object.keys(rqQuery).length > 0) {
      if (rqQuery['deliveryName']) {
        // если в rqQuery есть deliveryName, то заменяем его на deliveryAddress
        // для совместимости с API, который ожидает deliveryAddress для более глобального поиска
        rqQuery['deliveryAddress'] = rqQuery['deliveryName'];
        delete rqQuery['deliveryName'];
      }
      if (rqQuery['vin']) {
        // если в rqQuery есть vin, то заменяем его на carModelAndBrand
        // для совместимости с API, который ожидает carModelAndBrand для более глобального поиска
        rqQuery['carModelAndBrand'] = rqQuery['vin'];
        delete rqQuery['vin'];
      }
    }

    if (Object.keys(rqQuery).length > 0) {
      parsedParams['rqQuery'] = JSON.stringify(rqQuery);
    } else {
      delete parsedParams['rqQuery'];
    }

    return parsedParams;
  }

  private parseRqQuery(value: unknown): Record<string, unknown> {
    if (typeof value !== 'string') return {};

    try {
      const parsed: unknown = JSON.parse(value);

      return parsed !== null &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed)
        ? { ...parsed }
        : {};
    } catch {
      return {};
    }
  }

  loadRequest(requestNumber: string) {
    this.requestNumber$.next(requestNumber);
  }

  reloadRequest() {
    this.reload$.next();
  }

  refreshCurrentRequestsList() {
    const urlTree = this.router.parseUrl(this.router.url);

    urlTree.queryParams = {
      ...urlTree.queryParams,
      timestamp: Date.now(),
    };

    return this.router.navigateByUrl(urlTree);
  }

  get loadedRequests() {
    return this._$loadedRequests.asReadonly();
  }

  get loadedRequestsCount() {
    return this._$loadedRequestsCount.asReadonly();
  }

  get selectedRequestsIds() {
    return this._$selectedRequestsIds.asReadonly();
  }

  get selectedRequests() {
    return computed<RequestModel[]>(() => {
      const selectedRequestsIds = this.selectedRequestsIds();

      if (!selectedRequestsIds || !this.loadedRequests()) {
        return [];
      }

      return this.loadedRequests()!.filter(
        request => selectedRequestsIds![request.id],
      );
    });
  }

  get selectedRequestsCount(): Signal<number> {
    return computed(
      () =>
        Object.values(this.selectedRequestsIds()!).filter(value => value)
          .length,
    );
  }

  public setSelectedRequestsIds(selectedRequestsIds: SelectedIds | null) {
    if (!selectedRequestsIds) {
      this._$selectedRequestsIds.set(null);

      return;
    }
    this._$selectedRequestsIds.set({
      ...this.selectedRequestsIds(),
      ...selectedRequestsIds,
    });
  }

  public unselectFromSelectedRequestsIds(requests: RequestModel[]) {
    const newSelectedRequestsIds = { ...this.selectedRequestsIds() };

    requests.forEach(({ id }) => {
      if (newSelectedRequestsIds[id] !== undefined) {
        newSelectedRequestsIds[id] = false;
      }
    });
    this.setSelectedRequestsIds(newSelectedRequestsIds);
  }

  public unselectAllSelectedRequestsIds() {
    if (!this.selectedRequestsIds()) {
      return;
    }
    this.setSelectedRequestsIds(
      Object.keys(this.selectedRequestsIds()!).reduce(
        (acc, id) => ({ ...acc, [id]: false }),
        {},
      ),
    );
  }

  public setLoadedRequestsCount(count: number) {
    this._$loadedRequestsCount.set(count);
  }

  public setLoadedRequests(requests: RequestModel[] | null) {
    this._$loadedRequests.set(requests);
  }

  public reloadSelectedRequests$() {
    const selectedRequestIds = Object.entries(this.selectedRequestsIds() ?? {})
      .filter(([, selected]) => selected)
      .map(([id]) => Number(id));

    if (!selectedRequestIds.length) {
      console.warn('No selected requests to reload.');

      return EMPTY;
    }

    const requestIdBatches = Array.from(
      { length: Math.ceil(selectedRequestIds.length / 100) },
      (_, index) => selectedRequestIds.slice(index * 100, (index + 1) * 100),
    );

    return from(requestIdBatches).pipe(
      concatMap(requestIds =>
        this.requestsApiService.getAllRequests$(
          this.mapParamsToRqQuery({
            limit: requestIds.length,
            rqQuery: JSON.stringify({ id: requestIds }),
          }),
        ),
      ),
      map(({ items }) => items),
      toArray(),
      tap(requestBatches => {
        const requestsById = new Map(
          requestBatches.flat().map(request => [request.id, request]),
        );

        // find requests in loaded requests and update them
        const updatedRequests = this.loadedRequests()!.map(loadedRequest => {
          const request = requestsById.get(loadedRequest.id);

          return request ? new RequestModel(request) : loadedRequest;
        });

        this.setLoadedRequests(updatedRequests);
        this.selectedRequestsIds();
      }),
      catchError(e => {
        console.error('Failed to reload selected requests! Error:', e);

        return EMPTY;
      }),
    );
  }

  public loadSelectedRequests$() {
    const ids = Object.entries(this.selectedRequestsIds() ?? {})
      .filter(([, selected]) => selected)
      .map(([id]) => Number(id));

    if (!ids.length) {
      return EMPTY;
    }

    return this.requestsApiService
      .getAllRequests$({
        limit: ids.length,
        rqQuery: JSON.stringify({ id: ids }),
      })
      .pipe(
        map(({ items }) => items.map(request => new RequestModel(request))),
      );
  }

  public updateInspectionResult(
    initialInspectionResult: Nullable<boolean>,
    updatedInspectionResult: Nullable<boolean>,
  ) {
    return this.requestsApiService.updateInspectionResult(
      this.$request().id,
      initialInspectionResult,
      updatedInspectionResult,
    );
  }
}
